import * as React from "react";
import {
  Box,
  Card,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
} from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CircleIcon from "@mui/icons-material/Circle";
import QuizIcon from "@mui/icons-material/Quiz";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EmailIcon from "@mui/icons-material/Email";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import HandymanIcon from "@mui/icons-material/Handyman";
import Diversity1Icon from "@mui/icons-material/Diversity1";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import { motion, AnimatePresence } from "framer-motion";
import trainingbg from "../assets/training.jpg";
import Testimonials from "./Testimonials";
import FilmStripScroller from "./FilmStripScroller";
import LineBreak from "./LineBreak";

import img1 from "../assets/training/1.jpg";
import img2 from "../assets/training/2.jpg";
import img3 from "../assets/training/3.jpg";
import img4 from "../assets/training/4.jpg";
import img5 from "../assets/training/5.jpg";
import img6 from "../assets/training/6.jpg";
import img7 from "../assets/training/7.jpg";

const images = [img1, img2, img3, img4, img5, img6, img7];

const philoItems = [
  "戦術は状況によって変わる",
  "装備や任務内容は組織によって異なる",
  "速く正確に撃つための身体動作と射撃原理は普遍的である",
];

const notCoverItems = [
  "部隊戦術、CQB",
  "チームムーブメント",
  "任務や組織に特化した運営方法",
];

const focusesOnItems = [
  "銃器の安全な運用方法と正しいガンハンドリング",
  "射撃姿勢とフォーム",
  "グリッピング",
  "サイトの使い方と正しい視線の使い方",
  "トリガーコントロール",
  "リコイルコントロール",
  "ホルスタードロゥ",
  "リロード（マガジンチェンジ）",
  "ターゲット間のトランジション",
  "移動中のマズルコントロール",
  "スピードと精度のバランス",
  "ストレス下での射撃と身体動作",
  "ヒットファクター・スコアリングとマッチパフォーマンス指標の理解",
];

function TrainingOverview(): React.JSX.Element {
  return (
    <Box>
      <Box
        sx={{
          mt: 3,
          mx: "auto",
          width: { xs: "95%", md: "75%" },
          textAlign: "center",
        }}
      >
        <Typography variant="h4" color="text.primary" gutterBottom>
          トレーニング
        </Typography>
        <Typography variant="h6" color="text.primary" gutterBottom>
          <i>- 理論を学び、自分で上達できる力を身につける</i>
        </Typography>
      </Box>

      <Box sx={{ mt: 2, mx: "auto", width: { xs: "95%", md: "75%" } }}>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          私が行うトレーニングは、在米日本人（駐在員、学生、永住権保持者等）で銃器を護身用等に購入したばかりの初心者、趣味で銃の収集や射撃を楽しむ一般の方、これから実銃射撃を正しく学びたい方から、USPSA
          / IPSC / Steel Challenge
          などの射撃競技に取り組むシューターまで、幅広いレベルの方を対象としています。射撃技術向上を目的とする日本の警察・自衛隊関係者の方、日本でエアソフトガンを使用して、各種射撃競技、サバイバルゲーム等を楽しまれている人も含みます。
          <LineBreak />
          実銃の扱いに慣れていない初心者の方には、最初の段階から安全で責任ある正しい習慣を身につけるための基礎トレーニング
          を提供しています。
          <LineBreak />
          競技シューターに対しては、射撃理論と実射を通じて、速く、正確に、安全に、そして安定して撃てる射撃能力を高めることを目的としたトレーニングを行います。
          <LineBreak />
          私はこれまで、USPSA / IPSC、Steel
          Challengeなど、射撃スキルの限界が厳しく試される環境で経験を積んできました。また、NRA
          Certified
          Instructorとして、銃器安全管理および基礎射撃指導に関する正式な資格も保持しています。
          <LineBreak />
          このトレーニングは、「戦術（タクティクス）」を教えるものではありません。
          <LineBreak />
          目的は、すべての射撃の土台となる純粋な射撃の基礎力、つまり、あらゆる銃器使用に共通する普遍的な要素を、理論と実射の両面から理解し、身につけることです。
          <LineBreak />
          私のトレーニングの最終的な目標は、単にテクニックを覚えることではありません。射撃理論を深く理解し、自分自身で問題を診断し、修正し、継続的に上達できる力を身につけることです。
          <LineBreak />
          トレーニングは、主にアリゾナ州メサにある Rio Salado Sportsman’s Club
          で行います。リオ・サラド
          は、私が長年ホームレンジとして活動してきた場所です。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <PsychologyIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          トレーニングのコンセプト
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          競技射撃はスポーツです。しかし同時に、安全性、射撃のスピード、射撃精度、再現性、判断力が、リザルト（競技結果）と言う形で、非常に客観的に測定される数少ない射撃環境でもあります。巷では、タクトレと言う形でのトレーニングが「実践的」と思われがちですが、この手のトレーニングを行う人たちは競技への参加を躊躇うことが多いのが実情です。それは、リザルトと言う形で自分の射撃能力に対する評価が下ることを恐れていることが理由です。過去に特殊部隊に所属して実際に戦地へ派遣された経験のある人物が試合参加した際に、周りから見られていることで極度の緊張状態に陥り、手足が震えている状況を目にしたこともあります。その人は、以降、競技に出てくることはありませんでした。そのためアメリカでは、長年にわたり、トップレベルの競技シューターに対する信頼が最も高く、法執行機関、軍関係者、その他のプロフェッショナルユーザーに対して射撃指導を行う自然な関係が存在してきました。
          <LineBreak />
          その理由は明確です。
        </Typography>
        <List dense>
          {philoItems.map((item) => (
            <ListItem key={item}>
              <ListItemIcon>
                <CircleIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          私の役割は、その普遍的な基礎に集中し、それを高いレベルまで磨き上げ、役割、任務、装備に左右されない射撃の核心を教えることです。
          <LineBreak />
          良いコーチングとは、単に生徒に「正解」を与えることではありません。
          <LineBreak />
          本当に価値のあるトレーニングとは、生徒に「考え方」を教えることです。
          <LineBreak />
          空腹の人に食べ物を与えるのではなく、魚の捕り方を教えるというコーチングの基本的な考え方と同じように、私の目的は、シューター自身が射撃を理解し、問題を発見し、自分の力で上達していくための「射撃頭脳」を作り上げることです。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <QuizIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          戦術ではなく、射撃そのものを教える
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          このトレーニングでは、以下の内容は扱いません。
        </Typography>
        <List dense>
          {notCoverItems.map((item) => (
            <ListItem key={item}>
              <ListItemIcon
                sx={{
                  "& .MuiSvgIcon-root": {
                    color: "error.main",
                  },
                }}
              >
                <HighlightOffIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          これらは、組織の専門性、任務内容、環境によって大きく変わる要素です。
          <LineBreak />
          私のトレーニングで扱うのは、あくまで「射撃そのもの」です。
        </Typography>
        <List dense>
          {focusesOnItems.map((item) => (
            <ListItem key={item}>
              <ListItemIcon
                sx={{
                  "& .MuiSvgIcon-root": {
                    color: "success.main",
                  },
                }}
              >
                <CheckCircleOutlineIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          これらは、職業、競技種目、目的に関係なく、銃器を扱うすべての人に必要となる基本的な射撃スキルです。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <QuestionAnswerIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          お問い合わせ
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          競技射撃者、指導者、または射撃技術向上を目的とする警察・自衛隊関係者の方も、目的に応じたトレーニングについてご相談ください。クラス内容は、ニーズに応じて決めることが可能です。トレーニングに使用する銃器、装備品は貸出可能です。弾薬類は、時価でお支払い頂きます。
        </Typography>
      </Box>
      <Box
        sx={{
          mt: 3,
          mx: "auto",
          width: { xs: "95%", md: "75%" },
          textAlign: "center",
        }}
      >
        <Button
          component="a"
          href="mailto:sharkshooter2011@gmail.com"
          startIcon={<EmailIcon />}
          size="large"
          variant="contained"
          sx={{
            px: 5,
            py: 1.8,
            fontSize: "1.1rem",
            fontWeight: 600,
            borderRadius: 999,
            textTransform: "none",
            boxShadow: 3,
            transition: "all 0.25s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 6,
            },
          }}
        >
          お問い合わせ
        </Button>
      </Box>
    </Box>
  );
}

const philoInsideJPItems = [
  "速く、かつ正確に撃つための身体動作",
  "再現性のあるフォーム",
  "無駄な動きを排除した効率的な射撃プロセス",
  "射撃時の思考",
  "各技術における理論",
];

const airsoftItems = [
  "フォーム",
  "トリガー・コントロール",
  "グリッピング",
  "サイトアライメント／視線",
  "無駄な動作の有無",
  "プラクティカル・シューティングにおけるHF等のルールの理解",
  "スティールチャレンジにおける各技術、メンタルゲーム等",
];

const classInsideJP = [
  "一般公募は行いません",
  "信頼できるコーディネーターを通じて参加者を集めていただきます",
  "クラスはセミプライベート形式",
  "1グループ 最大10名まで",
];

const persons = ["初心者向け", "中級者向け", "上級・競技志向向け"];

const beginnerItems = [
  "安全確認と基本動作",
  "正確に撃つための基礎構造",
  "無駄な癖を作らないための初期習慣",
  "上達のために「最初に理解すべきこと」",
];

const classContent1Items = [
  "参加者の要望",
  "技術レベル",
  "競技経験",
  "レンジ環境",
];

const classContent2Items = [
  "なぜ上手くいかないのか",
  "何を変えるべきか",
  "どう練習すべきか",
];

const coordinatorItems = [
  "参加者の取りまとめ",
  "開催レンジとの連携",
  "技術レベルの調整",
  "クラス趣旨の事前共有",
  "当日の進行補助",
];

function TrainingInsideJP(): React.JSX.Element {
  return (
    <Box>
      <Box
        sx={{
          mt: 3,
          mx: "auto",
          textAlign: "center",
        }}
      >
        <Typography variant="h4" color="text.primary" gutterBottom>
          日本国内トレーニング
        </Typography>
      </Box>
      <Box sx={{ mt: 2, mx: "auto", width: { xs: "95%", md: "75%" } }}>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          以下は、実弾射撃・競技射撃で培った知見を、日本国内でどのように展開できるのか、その考え方、実施形式、開催条件をご案内するものです。エアソフトガンという手段を用いながらも、射撃技術の本質に妥協しないトレーニングを提供します。
          <LineBreak />
          本トレーニングは、アメリカ・アリゾナ州における実弾射撃・競技射撃で培ってきた技術、理論、評価基準を基盤とし、それらを日本の法制度下で実施可能な形として、エアソフトガンを用いて提供します。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <PsychologyIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          トレーニングの基本思想
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          本トレーニングでは、以下を重視します。
        </Typography>
        <List dense>
          {philoInsideJPItems.map((item) => (
            <ListItem key={item}>
              <ListItemIcon>
                <CircleIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          といった要素は、競技射撃・実射・法執行・防衛分野に共通する射撃技術の基礎構造であると考えています。アメリカでは、トップレベルのコンペティティブ・シューターが警察・軍関係者に対して射撃技術指導を行う事例が常識となっています。本クラスも同様に、「結果を出すための射撃技術」を競技で検証され続けてきた視点から体系的に伝えることを目的としています。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <HandymanIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          エアソフトガンの使用
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          日本国内では、法制度上、実弾射撃によるトレーニングが出来ません。その制約の中で、射撃技術の本質を損なわずに再現できる手段として、本クラスではエアソフトガンを使用します。
          <LineBreak />
          エアソフトガンであっても、
        </Typography>
        <List dense>
          {airsoftItems.map((item) => (
            <ListItem key={item}>
              <ListItemIcon>
                <CircleIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          などの要素は、実弾射撃と共通して評価・指導が可能です。
          <LineBreak />
          本トレーニングでは、「エアソフトガン特有の遊び方」ではなく、実弾射撃・競技射撃に通じる動作と考え方を前提とした内容を扱います。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <DashboardIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          クラス形式について
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          日本国内で実施するクラスは、招待制・コーディネーター主導型を基本としています。
        </Typography>
        <List dense>
          {classInsideJP.map((item) => (
            <ListItem key={item}>
              <ListItemIcon>
                <CircleIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          これは、トレーニングの質を最優先するための形式です。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <PersonIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          レベル別グループ編成について
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          本トレーニングでは、参加者の技術レベルを揃えたグループ編成を前提としています。
          <LineBreak />
          初心者・経験者・上級者が混在するクラスは行いません。
        </Typography>
        <List dense>
          {persons.map((item) => (
            <ListItem key={item}>
              <ListItemIcon>
                <CircleIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          といった形で、レベル別にグループを構成していただける場合に限り、クラス開催が可能です。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <SentimentVerySatisfiedIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          初心者向けクラスについて
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          初心者の方であっても、初心者のみで構成されたグループであれば、クラス開催は可能です。
          <LineBreak />
          その場合、
        </Typography>
        <List dense>
          {beginnerItems.map((item) => (
            <ListItem key={item}>
              <ListItemIcon>
                <CircleIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          といった、将来の成長を前提とした基礎内容を中心に指導します。
          <LineBreak />
          体験イベントではなく、「正しく始めるためのクラス」という位置づけです。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <LibraryBooksIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          クラス内容のカスタマイズ
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          クラス内容は、あらかじめ固定されたメニューを一律で実施するものではありません。
        </Typography>
        <List dense>
          {classContent1Items.map((item) => (
            <ListItem key={item}>
              <ListItemIcon>
                <CircleIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          を踏まえ、内容を調整・最適化します。 <LineBreak />
          各自の課題を明確にし、
        </Typography>
        <List dense>
          {classContent2Items.map((item) => (
            <ListItem key={item}>
              <ListItemIcon>
                <CircleIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          を具体的に示すことを重視しています。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <Diversity3Icon sx={{ verticalAlign: "middle", mr: 1 }} />
          コーディネーターについて
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          日本国内でのクラス開催には、コーディネーターの存在が不可欠です。
        </Typography>
        <List dense>
          {coordinatorItems.map((item) => (
            <ListItem key={item}>
              <ListItemIcon>
                <CircleIcon sx={{ mr: 1 }} />
              </ListItemIcon>
              <ListItemText primary={item} sx={{ color: "text.primary" }} />
            </ListItem>
          ))}
        </List>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          特別な資格や肩書きは必要ありません。クラスの意図を理解し、参加者の質を担保できる方であれば十分です。
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <ThumbUpOffAltIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          開催実績と評価について
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          2025年の日本滞在中には、エアソフトガンシューターを対象とした有料クラスを複数回開催しました。いずれも招待制・少人数形式で実施し、参加者からは高い評価と具体的なフィードバックをいただいています。
        </Typography>
        <Testimonials />
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ mt: 2 }}
        >
          <Diversity1Icon sx={{ verticalAlign: "middle", mr: 1 }} />
          クラス開催をご検討の方へ
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ mt: 1, lineHeight: 1.2 }}
        >
          日本国内でのクラス開催は、要望があり一定人数がまとまり、適切なコーディネーターが存在する場合に、実施を検討します。
          <LineBreak />
          本ページは、募集や告知のためのページではありません。
          <LineBreak />
          「このメンバーで学びたい」
          <LineBreak />
          「このレベルでクラスを組みたい」
          <LineBreak /> そうした形でのご相談を歓迎します。
        </Typography>
      </Box>
      <Box
        sx={{
          mt: 3,
          mx: "auto",
          width: { xs: "95%", md: "75%" },
          textAlign: "center",
        }}
      >
        <Button
          component="a"
          href="mailto:sharkshooter2011@gmail.com"
          startIcon={<EmailIcon />}
          size="large"
          variant="contained"
          sx={{
            px: 5,
            py: 1.8,
            fontSize: "1.1rem",
            fontWeight: 600,
            borderRadius: 999,
            textTransform: "none",
            boxShadow: 3,
            transition: "all 0.25s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 6,
            },
          }}
        >
          お問い合わせ
        </Button>
      </Box>
    </Box>
  );
}

export default function TrainingJpSection(): React.JSX.Element {
  const [view, setView] = React.useState<"overview" | "insideJP">("overview");
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          height: { xs: 300, md: 500 },
          backgroundImage: `url(${trainingbg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.25,
        }}
      />

      {/* Overlapping Text */}
      <Box
        id="training"
        sx={{
          pt: { xs: 14, md: 20 },
          px: { xs: 2, md: 0 },
          mt: { xs: "-200px", md: "-450px" }, // pulls text upward over image
          justifyContent: "center",
        }}
      >
        {/* ===== SWITCH BUTTONS (outside card) ===== */}
        <Box
          sx={{
            width: "100%",
            gap: 2,
            mb: 3,
            mx: "auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant={view === "overview" ? "contained" : "outlined"}
            size="large"
            onClick={() => setView("overview")}
            sx={{ textTransform: "none" }}
          >
            トレーニング
          </Button>

          <Button
            variant={view === "insideJP" ? "contained" : "outlined"}
            size="large"
            onClick={() => setView("insideJP")}
            sx={{ textTransform: "none" }}
          >
            日本国内トレーニング
          </Button>
        </Box>
        <Card
          elevation={3}
          sx={{
            mx: "auto",
            alignItems: "center",
            color: "white",
            width: { xs: "100%", lg: "60%" },
            borderRadius: 3,
          }}
        >
          <AnimatePresence mode="wait">
            {view === "overview" && (
              <motion.div
                key="overview-card"
                initial={{ x: "100%", scale: 0.97 }}
                animate={{ x: 0, scale: 1 }}
                exit={{ x: "-100%", scale: 0.97 }}
                transition={{
                  duration: 0.45,
                  ease: "easeInOut",
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <TrainingOverview />
              </motion.div>
            )}
            {view === "insideJP" && (
              <motion.div
                key="insideJP-card"
                initial={{ x: "100%", scale: 0.97 }}
                animate={{ x: 0, scale: 1 }}
                exit={{ x: "-100%", scale: 0.97 }}
                transition={{
                  duration: 0.45,
                  ease: "easeInOut",
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <TrainingInsideJP />
              </motion.div>
            )}
          </AnimatePresence>
          <Box sx={{ mt: 3 }}>
            <FilmStripScroller images={images} height={250} speed={20} />
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
